import { CarePlanInterventionResponse } from '~/lib/types/carePlan/carePlans';
import {
  ChakraBox,
  ChakraFlex,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  ModalV2,
  MultiSelectOption,
  Text,
  UniversalSelect,
  useToastMessage,
} from '@equip.health/ui';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useGetRefCarePlanInterventionsEnumQuery } from '~/store/services/refCarePlanEnums';
import Loading from '~/pages/loading';
import {
  useGetCarePlansByTreatmentEpisodeIdQuery,
  useUpdateInterventionMutation,
} from '~/store/services/carePlan';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';

const { neutral } = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'edit-intervention-assignees-modal';

type Props = {
  intervention: CarePlanInterventionResponse;
  setIsEditingAssigneesModalOpen: Dispatch<SetStateAction<boolean>>;
  treatmentEpisodeId: string;
};

export const EditInterventionAssigneesModal = ({
  intervention,
  setIsEditingAssigneesModalOpen,
  treatmentEpisodeId,
}: Props) => {
  const { createToast } = useToastMessage();

  const {
    data: refInterventions,
    isLoading: isGetPathwayInterventionsLoading,
  } = useGetRefCarePlanInterventionsEnumQuery({});
  const { data: carePlans, isLoading: isGetCarePlansLoading } =
    useGetCarePlansByTreatmentEpisodeIdQuery(treatmentEpisodeId);
  const [
    updateIntervention,
    {
      isLoading: isUpdateInterventionLoading,
      isError: isUpdateInterventionError,
    },
  ] = useUpdateInterventionMutation();

  useEffect(() => {
    if (isUpdateInterventionError) {
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: 'edit-intervention-assignees-error_toast',
        text: "We're sorry, we were unable to update the assignees. Please try again later",
        variant: 'critical',
      });
    }
  }, [isUpdateInterventionError]);

  const [assignedProviderTypes, setAssignedProviderTypes] = useState<string[]>(
    intervention.roles,
  );

  const activeCarePlan = carePlans?.find(
    (pathway) => pathway.status.code === CarePlanStatus.ACTIVE,
  );

  const carePlanModalityCode =
    activeCarePlan?.treatmentEpisodeModality?.modality.code;

  const refIntervention = refInterventions?.find(
    (ri) => ri.code === intervention.intervention.code,
  );

  const options: MultiSelectOption[] =
    refIntervention?.providerTypes
      .filter((pt) => pt.modalityCode === carePlanModalityCode)
      .map((pt) => ({
        label: pt.providerType,
        value: pt.providerType,
      })) || [];

  const isEditingDisabled = options.length < 2;

  return (
    <ModalV2
      isOpen
      onClose={() => {
        setIsEditingAssigneesModalOpen(false);
      }}
      size="3xl"
      title="Edit intervention"
    >
      {(isGetPathwayInterventionsLoading || isGetCarePlansLoading) && (
        <Loading />
      )}
      {!isGetPathwayInterventionsLoading && !isGetCarePlansLoading && (
        <>
          <ChakraBox
            border={`1px solid ${neutral.line}`}
            borderRadius="12px"
            marginBottom="24px"
            padding="18px"
          >
            <Text marginBottom="24px">{intervention.intervention.label}</Text>
            <Text marginBottom="9px" variant="bodyMedium">
              Assigned to
            </Text>
            <UniversalSelect
              ariaLabel="Assigned to"
              id={`${TOP_LEVEL_ID}-select`}
              isDisabled={isEditingDisabled}
              isSingleSelect={false}
              onChange={(newOptions: MultiSelectOption[]) => {
                setAssignedProviderTypes(
                  newOptions.map((option) => option.value),
                );
              }}
              options={options}
              value={options.filter((option) =>
                assignedProviderTypes.includes(option.value),
              )}
              withCheckBox
            />
          </ChakraBox>
          <ChakraFlex justifyContent="space-between" width="100%">
            <FireflyButton
              id={`${TOP_LEVEL_ID}__cancel`}
              leftIcon={FAIconName.timesRegular}
              onClick={() => {
                setIsEditingAssigneesModalOpen(false);
              }}
              variant="secondary"
            >
              Cancel
            </FireflyButton>
            <FireflyButton
              id={`${TOP_LEVEL_ID}__confirm`}
              isDisabled={
                isEditingDisabled || assignedProviderTypes.length === 0
              }
              isLoading={isUpdateInterventionLoading}
              leftIcon={FAIconName.checkRegular}
              onClick={() => {
                updateIntervention({
                  treatmentEpisodeId,
                  interventionId: intervention.id,
                  body: {
                    providerTypes: assignedProviderTypes,
                  },
                }).then(() => {
                  setIsEditingAssigneesModalOpen(false);
                });
              }}
            >
              Save changes
            </FireflyButton>
          </ChakraFlex>
        </>
      )}
    </ModalV2>
  );
};
